import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, interval } from 'rxjs';

export interface GeolocationError {
  code: number;
  message: string;
  type: 'PERMISSION_DENIED' | 'POSITION_UNAVAILABLE' | 'TIMEOUT' | 'UNKNOWN';
}

export interface LocationData {
  lat: number;
  lng: number;
}

@Injectable({ providedIn: 'root' })
export class LocationService {
  private readonly CURRENT_LOCATION_KEY = 'user_current_location';
  private readonly LOCATION_TIMESTAMP_KEY = 'user_location_timestamp';
  private readonly LOCATION_EXPIRY_HOURS = 24;
  private readonly LOCATION_UPDATE_INTERVAL = 2 * 60 * 1000; // 2 minutes
  private readonly LOCATION_ERROR_COOLDOWN = 5 * 60 * 1000; // 5 minutes

  private currentLocationSubject = new BehaviorSubject<LocationData | null>(null);
  public currentLocation$ = this.currentLocationSubject.asObservable();

  private locationUpdateSubscription?: Subscription;
  private autoLocationUpdateEnabled = false;
  private lastLocationErrorTime: number = 0;

  constructor() {
    this.loadSavedLocation();
  }

  /**
   * Check if geolocation is supported by the browser
   */
  isGeolocationSupported(): boolean {
    return this.isProviderAvailable();
  }

  /**
   * Get current location once with custom options
   */
  getCurrentLocation(options?: PositionOptions): Observable<LocationData> {
    return new Observable(observer => {
      if (!this.isGeolocationSupported()) {
        observer.error({
          code: 0,
          message: 'Geolokalizacja nie jest wspierana przez Twoją przeglądarkę.',
          type: 'UNKNOWN'
        } as GeolocationError);
        return;
      }

      const defaultOptions: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000
      };

      this.getPositionFromProvider(
        (position) => {
          const location: LocationData = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          this.currentLocationSubject.next(location);
          this.saveCurrentLocation(location);
          observer.next(location);
          observer.complete();
        },
        (error) => {
          observer.error(this.mapGeolocationError(error));
        },
        { ...defaultOptions, ...options }
      );
    });
  }

  /**
   * Get current location with standard error handling
   */
  getCurrentLocationWithErrorHandling(): Observable<LocationData> {
    return new Observable(observer => {
      this.getCurrentLocation().subscribe({
        next: (location) => {
          observer.next(location);
          observer.complete();
        },
        error: (error: GeolocationError) => {
          let errorMessage = 'Nie można pobrać lokalizacji: ';
          switch (error.type) {
            case 'PERMISSION_DENIED':
              errorMessage += 'Dostęp do lokalizacji został zablokowany. Sprawdź ustawienia przeglądarki.';
              break;
            case 'POSITION_UNAVAILABLE':
              errorMessage += 'Informacje o lokalizacji są niedostępne.';
              break;
            case 'TIMEOUT':
              errorMessage += 'Przekroczono czas oczekiwania na pobranie lokalizacji.';
              break;
            default:
              errorMessage += error.message;
              break;
          }
          observer.error({ ...error, message: errorMessage });
        }
      });
    });
  }

  /**
   * Update current location silently without alerts
   */
  updateLocationSilently(minDistanceMeters: number = 10): void {
    if (!this.isGeolocationSupported()) {
      return;
    }

    this.getPositionFromProvider(
      (position) => {
        const newLocation: LocationData = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };

        // Check if location has changed significantly
        const currentLoc = this.currentLocationSubject.value;
        if (currentLoc && minDistanceMeters > 0) {
          const distance = this.calculateDistance(currentLoc, newLocation);
          if (distance < minDistanceMeters) {
            return; // Location hasn't changed significantly
          }
        }

        this.currentLocationSubject.next(newLocation);
        this.saveCurrentLocation(newLocation);
      },
      (error) => {
        // Handle permission denied by stopping auto updates
        if (error.code === error.PERMISSION_DENIED) {
          this.stopAutoLocationUpdate();
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  }

  /**
   * Start automatic location updates
   */
  startAutoLocationUpdate(): void {
    if (this.locationUpdateSubscription) {
      this.stopAutoLocationUpdate();
    }

    this.autoLocationUpdateEnabled = true;
    this.locationUpdateSubscription = interval(this.LOCATION_UPDATE_INTERVAL).subscribe(() => {
      this.updateLocationSilently();
    });
  }

  /**
   * Stop automatic location updates
   */
  stopAutoLocationUpdate(): void {
    if (this.locationUpdateSubscription) {
      this.locationUpdateSubscription.unsubscribe();
      this.locationUpdateSubscription = undefined;
    }
    this.autoLocationUpdateEnabled = false;
  }

  /**
   * Toggle automatic location updates
   */
  toggleAutoLocationUpdate(): Observable<boolean> {
    return new Observable(observer => {
      if (!this.isGeolocationSupported()) {
        observer.error({
          code: 0,
          message: 'Geolokalizacja nie jest wspierana przez Twoją przeglądarkę.',
          type: 'UNKNOWN'
        } as GeolocationError);
        return;
      }

      if (this.autoLocationUpdateEnabled) {
        this.stopAutoLocationUpdate();
        observer.next(false);
        observer.complete();
        return;
      }

      // Test permission before starting auto updates
      this.getPositionFromProvider(
        () => {
          this.startAutoLocationUpdate();
          observer.next(true);
          observer.complete();
        },
        (error) => {
          observer.error(this.mapGeolocationError(error));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    });
  }

  /**
   * Get auto location update status
   */
  isAutoUpdateEnabled(): boolean {
    return this.autoLocationUpdateEnabled;
  }

  /**
   * Get auto location update status text
   */
  getAutoLocationUpdateStatus(): string {
    return this.autoLocationUpdateEnabled
      ? 'Automatyczne aktualizacje włączone'
      : 'Automatyczne aktualizacje wyłączone';
  }

  /**
   * Get current location value (synchronous)
   */
  getCurrentLocationValue(): LocationData | null {
    return this.currentLocationSubject.value;
  }

  /**
   * Get saved location info for display
   */
  getSavedLocationInfo(): string | null {
    try {
      const timestampStr = localStorage.getItem(this.LOCATION_TIMESTAMP_KEY);
      if (timestampStr && this.currentLocationSubject.value) {
        const timestamp = parseInt(timestampStr);
        const now = Date.now();
        const hoursDiff = (now - timestamp) / (1000 * 60 * 60);

        if (hoursDiff < 1) {
          const minutesDiff = Math.floor((now - timestamp) / (1000 * 60));
          return `Lokalizacja zapisana ${minutesDiff} min temu`;
        } else {
          return `Lokalizacja zapisana ${Math.floor(hoursDiff)} godz. temu`;
        }
      }
    } catch (error) {
      console.warn('Failed to get saved location info:', error);
    }
    return null;
  }

  /**
   * Clear saved location from storage
   */
  clearSavedLocation(): void {
    try {
      localStorage.removeItem(this.CURRENT_LOCATION_KEY);
      localStorage.removeItem(this.LOCATION_TIMESTAMP_KEY);
      this.currentLocationSubject.next(null);
    } catch (error) {
      console.warn('Failed to clear saved location:', error);
    }
  }

  /**
   * Check if should show location error (rate limiting)
   */
  shouldShowLocationError(): boolean {
    const now = Date.now();
    if (now - this.lastLocationErrorTime > this.LOCATION_ERROR_COOLDOWN) {
      this.lastLocationErrorTime = now;
      return true;
    }
    return false;
  }

  /**
   * Calculate distance between two points in meters
   */
  private calculateDistance(from: LocationData, to: LocationData): number {
    const R = 6371e3; // Earth radius in meters
    const φ1 = from.lat * Math.PI / 180;
    const φ2 = to.lat * Math.PI / 180;
    const Δφ = (to.lat - from.lat) * Math.PI / 180;
    const Δλ = (to.lng - from.lng) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) *
      Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  /**
   * Save current location to localStorage
   */
  private saveCurrentLocation(location: LocationData): void {
    try {
      const locationData = {
        lat: location.lat,
        lng: location.lng,
        timestamp: Date.now()
      };
      localStorage.setItem(this.CURRENT_LOCATION_KEY, JSON.stringify(locationData));
      localStorage.setItem(this.LOCATION_TIMESTAMP_KEY, locationData.timestamp.toString());
    } catch (error) {
      console.warn('Failed to save location to localStorage:', error);
    }
  }

  /**
   * Load saved location from localStorage
   */
  private loadSavedLocation(): void {
    try {
      const savedLocationStr = localStorage.getItem(this.CURRENT_LOCATION_KEY);
      const timestampStr = localStorage.getItem(this.LOCATION_TIMESTAMP_KEY);

      if (savedLocationStr && timestampStr) {
        const savedLocation = JSON.parse(savedLocationStr);
        const timestamp = parseInt(timestampStr);
        const now = Date.now();
        const hoursDiff = (now - timestamp) / (1000 * 60 * 60);

        // Check if location is not expired
        if (hoursDiff < this.LOCATION_EXPIRY_HOURS) {
          const location: LocationData = {
            lat: savedLocation.lat,
            lng: savedLocation.lng
          };
          this.currentLocationSubject.next(location);
        } else {
          // Remove expired location
          this.clearSavedLocation();
        }
      }
    } catch (error) {
      console.warn('Failed to load saved location:', error);
      this.clearSavedLocation();
    }
  }

  /**
   * Map GeolocationPositionError to our error interface
   */
  private mapGeolocationError(error: GeolocationPositionError): GeolocationError {
    let type: GeolocationError['type'] = 'UNKNOWN';

    switch (error.code) {
      case error.PERMISSION_DENIED:
        type = 'PERMISSION_DENIED';
        break;
      case error.POSITION_UNAVAILABLE:
        type = 'POSITION_UNAVAILABLE';
        break;
      case error.TIMEOUT:
        type = 'TIMEOUT';
        break;
    }

    return {
      code: error.code,
      message: error.message,
      type
    };
  }

  /**
   * Get current position from the geolocation provider
   * This method wraps the native geolocation API and can be easily replaced
   * with a different provider (e.g., mock, custom GPS, etc.)
   */
  private getPositionFromProvider(
    successCallback: (position: GeolocationPosition) => void,
    errorCallback: (error: GeolocationPositionError) => void,
    options?: PositionOptions
  ): void {
    navigator.geolocation.getCurrentPosition(
      successCallback,
      errorCallback,
      options
    );
  }

  /**
   * Check if the geolocation provider is available
   * This method wraps the provider availability check and can be easily replaced
   * when using a different location provider
   */
  private isProviderAvailable(): boolean {
    return !!navigator?.geolocation;
  }
}
